import { motion } from 'framer-motion';
import { Users, Shield, Loader, AlertTriangle } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import { supabase } from '../lib/supabase';
import { isCurrentUserAdmin, getCurrentUserAdminInfo } from '../utils/adminAccess';

interface DashboardSelectorProps {
  onNavigate: (view: string) => void;
}

const DashboardSelector: React.FC<DashboardSelectorProps> = ({ onNavigate }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<'user' | 'admin' | 'unknown'>('unknown');
  const [userInfo, setUserInfo] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    determineUserRole();
  }, []);

  const determineUserRole = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Get current user info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('No user found. Please log in again.');
        return;
      }

      setUserInfo(user);

      // Check if user is admin
      const isAdmin = await isCurrentUserAdmin();
      
      if (isAdmin) {
        const adminInfo = await getCurrentUserAdminInfo();
        setUserRole('admin');
        console.log('🔐 Admin user detected:', adminInfo);
      } else {
        setUserRole('user');
        console.log('👤 Regular user detected:', user);
      }
    } catch (error) {
      console.error('Error determining user role:', error);
      setError('Error determining user access level.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDashboardAccess = () => {
    if (userRole === 'admin') {
      onNavigate('admin');
    } else {
      onNavigate('dashboard');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 bg-[var(--vscode-primary)]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Determining Access Level</h2>
          <p className="text-gray-400">Please wait while we verify your permissions...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Access Error</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => onNavigate('login')}
            className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors"
          >
            Return to Login
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-8">
          <div className="text-center mb-8">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              userRole === 'admin' ? 'bg-red-500/20' : 'bg-[var(--vscode-primary)]/20'
            }`}>
              {userRole === 'admin' ? (
                <Shield className="w-8 h-8 text-red-400" />
              ) : (
                <Users className="w-8 h-8 text-cyan-400" />
              )}
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">
              {userRole === 'admin' ? 'Admin Access Detected' : 'User Dashboard'}
            </h1>
            
            <p className="text-gray-400 mb-4">
              {userRole === 'admin' 
                ? 'You have administrative privileges and can access all system features.'
                : 'Welcome to your personal job search workspace.'
              }
            </p>

            {userInfo && (
              <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-300">
                  <strong>User:</strong> {userInfo.name || userInfo.email}
                </p>
                <p className="text-sm text-gray-300">
                  <strong>Role:</strong> {userRole === 'admin' ? 'Administrator' : 'User'}
                </p>
                {userRole === 'admin' && (
                  <p className="text-sm text-gray-300">
                    <strong>Permissions:</strong> Full System Access
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <button
              onClick={handleDashboardAccess}
              className={`w-full py-4 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
                userRole === 'admin'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-cyan-600 hover:bg-cyan-700 text-white'
              }`}
            >
              {userRole === 'admin' ? (
                <>
                  <Shield className="w-5 h-5" />
                  <span>Access Admin Dashboard</span>
                </>
              ) : (
                <>
                  <Users className="w-5 h-5" />
                  <span>Go to User Dashboard</span>
                </>
              )}
            </button>

            <button
              onClick={() => onNavigate('login')}
              className="w-full py-3 px-6 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
            >
              Switch Account
            </button>
          </div>

          {userRole === 'admin' && (
            <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <h3 className="text-sm font-medium text-red-400 mb-2">Admin Notice:</h3>
              <p className="text-xs text-gray-400">
                You have full administrative access to all system features, user management, 
                and system monitoring tools. Use this power responsibly.
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default DashboardSelector;
